import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate
} from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { CallbackPage } from './auth/CallbackPage';
import { LoginPage } from './auth/LoginPage';
import { RequireAuth } from './auth/RequireAuth';
import { SessionExpiredPage } from './auth/SessionExpiredPage';
import { usePostMessageAuth } from './auth/usePostMessageAuth';
import { useTokenRefresh } from './auth/useTokenRefresh';
import { useTerminalAccount } from './context/AccountContext';
import { useTranslation } from '@src/i18n';
import { LoadingPage } from './components/LoadingPage';
import { SelectionPage } from './components/SelectionPage';
import { PunchPadPage } from './components/PunchPadPage';
import { SettingsPage } from './components/SettingsPage/SettingsPage';
import { AppFooter } from './components/AppFooter';
import { TopBar } from './components/TopBar';
import { Button, Text, Stack } from '@7shifts/sous-chef-base';

function AccountErrorPage({
    message,
    onRetry
}: {
    message: string;
    onRetry: () => void;
}) {
    const { t } = useTranslation();
    return (
        <main
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                padding: 'var(--spacing-32)',
                gap: 'var(--spacing-20)'
            }}
        >
            <Stack space={20} alignItems="center">
                <Stack space={8} alignItems="center">
                    <Text as="h1" alignment="center">
                        {t('error.defaultHeader')}
                    </Text>
                    <Text
                        as="h4"
                        alignment="center"
                        color="var(--color-surface-on-color-subtle)"
                    >
                        {message}
                    </Text>
                </Stack>
                <Button
                    type="button"
                    theme="primary"
                    size="full-width"
                    onClick={onRetry}
                    testId="account-error-retry"
                >
                    {t('common.tryAgain')}
                </Button>
            </Stack>
        </main>
    );
}

function TerminalShell() {
    const {
        account,
        error,
        clearAccount,
        needsCompanyPick,
        needsLocationPick
    } = useTerminalAccount();
    const { t } = useTranslation();

    if (error) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <AccountErrorPage
                    message={t(error as Parameters<typeof t>[0])}
                    onRetry={clearAccount}
                />
            </div>
        );
    }

    if (account === null) {
        // Only show the selection UI when the user actually needs to pick
        // something. Otherwise the terminal is still hydrating (fetching
        // companies/locations/account) and we show a loading screen instead
        // of a flash of disabled dropdowns.
        const needsUserInput = needsCompanyPick || needsLocationPick;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {needsUserInput ? <SelectionPage /> : <LoadingPage />}
            </div>
        );
    }

    return <PunchPadPage />;
}

/**
 * Inner shell rendered inside BrowserRouter so it can use routing hooks.
 * Owns the TopBar props and route definitions.
 */
function AppShell({ isAuthenticated }: { isAuthenticated: boolean }) {
    const navigate = useNavigate();
    const location = useLocation();
    const isSettingsPage = location.pathname === '/settings';

    function handleLeftAction() {
        if (isSettingsPage) {
            navigate(-1);
        } else {
            void navigate('/settings');
        }
    }
    return (
        <>
            <TopBar
                showLeftAction={isAuthenticated}
                showBack={isSettingsPage}
                onLeftAction={isAuthenticated ? handleLeftAction : undefined}
            />
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/callback" element={<CallbackPage />} />
                <Route path="/" element={<Navigate to="/terminal" replace />} />
                <Route
                    path="/terminal"
                    element={
                        <RequireAuth>
                            <TerminalShell />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/settings"
                    element={
                        <RequireAuth>
                            <SettingsPage />
                        </RequireAuth>
                    }
                />
                <Route path="*" element={<Navigate to="/terminal" replace />} />
            </Routes>
            <AppFooter />
        </>
    );
}

function App() {
    const { isAuthenticated, isSessionExpired } = useAuth();
    usePostMessageAuth();
    useTokenRefresh();

    if (isSessionExpired) {
        return <SessionExpiredPage />;
    }

    return (
        <BrowserRouter>
            <AppShell isAuthenticated={isAuthenticated} />
        </BrowserRouter>
    );
}

export default App;
